import { MuseumObject } from "@/backend/types/domain";
import * as React from "react";
import { ImageWithPlaceholder } from "./ImageWithPlaceholder";
import { MediaCard } from "./MediaCard";
import { getCultureWikipediaUrl } from "../lib/cultureWikipedia";
import { useFilterContext } from "../context/FilterContext";
import { trackEvent } from "../lib/analytics";
import { MAX_CARD_IMAGES } from "../lib/mediaCard";

/*
LOG:
 - Oct 29 2025: add a few more details in here, like the country, tribe, estimated year of creation
 - Nov 2 2026: image-first layout on the shared MediaCard; flush grid
*/

/**
 * This is to fix/workaround an issue where some of our image URLs wrappped in a proxier
 * are prefixed with localhost instead of our live hostname
 * @param imageUrl
 * @returns
 */
export function fixProxiedImageUrl(imageUrl: string) {
  // Guarded because this runs during render on whatever happens to be stored:
  // a relative or malformed URL in one row would otherwise throw and blank the
  // entire results grid. Degrading to the raw string lets that one image fail
  // on its own. (#29, step 5.)
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(imageUrl);
  } catch {
    return imageUrl;
  }

  if (
    parsedUrl.hostname.includes("localhost") &&
    window.location.hostname !== "localhost"
  ) {
    parsedUrl.protocol = "https:";
    parsedUrl.hostname = "artifacts-index.surulere.dev";
    parsedUrl.port = "";
  }
  return parsedUrl.toString();
}

/**
 * Which URLs to try for one image, in order.
 *
 * `src` is our mirrored copy when the backend found one, and `fallbackSrc` is
 * the museum's own URL. With no mirror the museum URL is simply `src` and
 * there is nothing to fall back to — which is every image until a mirror run
 * has reached it, so this has to read as unremarkable.
 */
export function artifactImageSources(
  obj: MuseumObject,
  img: { url?: string; mirrorUrl?: string },
): { src: string; fallbackSrc?: string } {
  const museumUrl = obj.dataSource?.museum?.toLowerCase().includes("smithsonian")
    ? getUnproxiedImageUrl(img.url!)
    : fixProxiedImageUrl(img.url!);

  return img.mirrorUrl
    ? { src: img.mirrorUrl, fallbackSrc: museumUrl }
    : { src: museumUrl };
}

export function getUnproxiedImageUrl(proxiedImageUrl: string): string {
  const decodedReqUrl = decodeURIComponent(proxiedImageUrl);
  const startOfUnproxied = decodedReqUrl.indexOf("https://", 4);
  return decodedReqUrl.slice(startOfUnproxied);
}

/**
 * How roomy cards are. Applied to the grid (`su-card-grid--<mode>`), which
 * sets the tile width and image ratio; the card only decides which rows show.
 */
export type CardViewMode = "compact" | "standard" | "detailed";

export const MuseumObjectCard = ({
  obj,
  showDetail,
  viewMode = "standard",
  badge,
}: {
  obj: MuseumObject;
  showDetail: (obj: MuseumObject) => void;
  viewMode?: CardViewMode;
  badge?: React.ReactNode;
}) => {
  const filterContext = useFilterContext();
  const isCompact = viewMode === "compact";
  const isDetailed = viewMode === "detailed";

  const images = React.useMemo(
    () => (obj.images ?? []).filter((img) => img.url).slice(0, MAX_CARD_IMAGES),
    [obj],
  );
  const culture = obj.culture;
  const cultureWikiUrl = culture ? getCultureWikipediaUrl(culture) : undefined;
  const museum = obj.dataSource?.museum;
  const sourceUrl = formatSourceUrl(obj);

  return (
    <MediaCard
      resetKey={obj.id ?? obj.title}
      slideCount={images.length}
      renderSlide={(i) => (
        <ImageWithPlaceholder
          {...artifactImageSources(obj, images[i])}
          museum={museum}
          alt={obj.title || "Artifact image"}
          className="su-media-img"
        />
      )}
      emptyMedia={<span>No image{museum ? ` · ${museum}` : ""}</span>}
      onSlideChange={(_, method) => trackEvent("card_image_step", { method })}
      onOpen={() => showDetail(obj)}
      badge={badge}
      title={obj.title || "Untitled"}
      meta={
        (culture || (!isCompact && obj.date?.display)) && (
          <>
            {culture &&
              (cultureWikiUrl ? (
                <a
                  href={cultureWikiUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {culture} ↗
                </a>
              ) : (
                <span>{culture}</span>
              ))}
            {culture && filterContext && !isCompact && (
              <button
                type="button"
                className="su-card-action"
                title={`Filter by "${culture}"`}
                aria-label={`Filter by ${culture}`}
                onClick={(e) => {
                  e.stopPropagation();
                  trackEvent("filter_by_culture", { culture, source: "card" });
                  filterContext.filterByCulture(culture);
                }}
              >
                ⚲
              </button>
            )}
            {!isCompact && obj.date?.display && (
              <span>
                {culture ? "· " : ""}
                {obj.date.display}
              </span>
            )}
          </>
        )
      }
    >
      {isDetailed && obj.artists?.length ? (
        <p className="su-card-detail">
          <span className="su-card-detail-label">Artists</span>
          {obj.artists.map((artist) => artist.name).join(", ")}
        </p>
      ) : null}
      {isDetailed && obj.materials && obj.materials.length > 0 && (
        <p className="su-card-detail">
          <span className="su-card-detail-label">Materials</span>
          {obj.materials}
        </p>
      )}
      {isDetailed && obj.provenance && (
        <p className="su-card-detail">
          <span className="su-card-detail-label">Provenance</span>
          {obj.provenance}
        </p>
      )}
      {!isCompact && museum && (
        <div className="su-card-meta">
          {sourceUrl ? (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {museum} ↗
            </a>
          ) : (
            <span>{museum}</span>
          )}
          {isDetailed && obj.objectNumber && (
            <span title={obj.objectNumber}>· {obj.objectNumber}</span>
          )}
        </div>
      )}
    </MediaCard>
  );
};

export function formatSourceUrl(obj: MuseumObject): string | undefined {
  return obj.dataSource?.url && obj.dataSource.url.includes("artic.edu")
    ? obj.dataSource.url.replace(
      /^https:\/\/api\.artic\.edu\/api\/v1\/artworks\/(\d+).*/,
      "https://artic.edu/artworks/$1"
    )
    : obj.dataSource?.url;
}
